import React, { useState } from 'react';
import { Search, Calendar, DollarSign, Building2, FileText, X, Loader, Package, Award } from 'lucide-react';
import { useData } from '../contexts/DataContext';
import { Tender, TenderItem } from '../types';

interface MercadoPublicoTender {
  Codigo: string;
  Nombre: string;
  Descripcion: string;
  FechaPublicacion: string;
  FechaCierre: string;
  CodigoExterno: string;
  MontoEstimado: number;
  Moneda: string;
  Tiempo: number;
  Comprador: {
    NombreOrganismo: string;
    RutUnidad: string;
    ComunaUnidad: string;
    RegionUnidad: string;
    NombreUsuario: string;
  };
  Fechas: {
    FechaCreacion: string;
    FechaCierre: string;
    FechaFinal?: string;
    FechaAdjudicacion?: string;
    FechaVisitaTerreno?: string;
  };
  Organismo: {
    Nombre: string;
  };
  Estado: string; // Estado directo de la API
  Tipo: {
    Nombre: string;
  };
  Items?: {
    Cantidad: number;
    Listado?: Array<{
      CodigoProducto?: string;
      NombreProducto: string;
      UnidadMedida?: string;
      Cantidad: number;
      Adjudicacion?: {
        RutProveedor: string;
        NombreProveedor: string;
        Cantidad: number;
        MontoUnitario: number;
      };
    }>;
  };
}

interface TenderSearchProps {
  onClose: () => void;
}

const TenderSearch: React.FC<TenderSearchProps> = ({ onClose }) => {
  const { organizations, organizationTypes, tenderTypes, tenderStatuses, applicationStatuses, tenders, addOrganization, addTender } = useData();
  const [searchId, setSearchId] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [tender, setTender] = useState<MercadoPublicoTender | null>(null);
  const [error, setError] = useState('');
  const [tenderExists, setTenderExists] = useState(false);

  const handleSearch = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!searchId.trim()) {
      setError('Por favor ingresa un ID de licitación');
      return;
    }

    setIsLoading(true);
    setError('');
    setTender(null);
    setTenderExists(false);

    try {
      const response = await fetch(
        `https://api.mercadopublico.cl/servicios/v1/publico/licitaciones.json?codigo=${searchId.trim()}&ticket=F8537A18-6766-4DEF-9E59-426B4FEE2844`
      );

      if (!response.ok) {
        throw new Error('Error al consultar la API de Mercado Público');
      }

      const data = await response.json();
      
      if (data.Listado && data.Listado.length > 0) {
        const foundTender = data.Listado[0];
        setTender(foundTender);
        
        // Verificar si la licitación ya existe en la base de datos
        const existingTender = tenders.find(t => t.tender_code === foundTender.CodigoExterno);
        if (existingTender) {
          setTenderExists(true);
        }
      } else {
        setError('No se encontró ninguna licitación con ese ID');
      }
    } catch (err) {
      console.error('Error searching tender:', err);
      setError('Error al buscar la licitación. Verifica el ID e intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  // Helper function to map currencies from Mercado Público to our supported currencies
  const mapCurrency = (apiCurrency: string): 'CLP' | 'USD' | 'EUR' => {
    const currencyMap: Record<string, 'CLP' | 'USD' | 'EUR'> = {
      'Peso': 'CLP',
      'CLP': 'CLP',
      'UF': 'CLP', // Map UF (Unidad de Fomento) to CLP
      'USD': 'USD',
      'EUR': 'EUR',
      'Dólar': 'USD',
      'Euro': 'EUR'
    };

    return currencyMap[apiCurrency] || 'CLP'; // Default to CLP if currency not recognized
  };

  const handleImportTender = async () => {
    if (!tender) return;

    setIsLoading(true);
    try {
      // 1. Crear organización si no existe
      let organizationId = '';
      const existingOrg = organizations.find(org => 
        org.name === tender.Comprador.NombreOrganismo
      );

      if (existingOrg) {
        organizationId = existingOrg.id;
      } else {
        // Buscar el tipo "Servicio Público"
        const publicServiceType = organizationTypes.find(type => 
          type.name === 'Servicio Público'
        );

        const newOrgData = {
          name: tender.Comprador.NombreOrganismo,
          organization_type_id: publicServiceType?.id || organizationTypes[0]?.id || '',
          comuna: tender.Comprador.ComunaUnidad,
          region: tender.Comprador.RegionUnidad,
          rut: tender.Comprador.RutUnidad,
          contact: tender.Comprador.NombreUsuario,
          email: '' // No disponible en la API
        };

        // Crear la organización y obtener su ID
        const createdOrg = await addOrganization(newOrgData);
        organizationId = createdOrg?.id || '';
      }

      // 2. Buscar tipo de licitación "Pública"
      const publicTenderType = tenderTypes.find(type => 
        type.name === 'Pública'
      );

      // Si no existe el tipo "Pública", usar el primero disponible
      const tenderTypeId = publicTenderType?.id || tenderTypes[0]?.id || '';

      // 3. Buscar estado que coincida con el estado de la API
      // Primero intentar encontrar un estado que coincida exactamente
      let matchingStatus = tenderStatuses.find(status => 
        status.name === tender.Estado
      );

      // Si no encuentra coincidencia exacta, usar mapeo común
      if (!matchingStatus) {
        const statusMapping: Record<string, string> = {
          'Publicada': 'Publicada',
          'Cerrada': 'Cerrada',
          'Adjudicada': 'Adjudicada',
          'Desierta': 'Desierta',
          'Revocada': 'Revocada',
          'Suspendida': 'Suspendida'
        };

        const mappedStatusName = statusMapping[tender.Estado] || tender.Estado;
        matchingStatus = tenderStatuses.find(status => 
          status.name === mappedStatusName
        );
      }

      // Si aún no encuentra, usar el primer estado disponible como fallback
      const statusId = matchingStatus?.id || tenderStatuses[0]?.id || '';

      // 4. Buscar estado de postulación "Pendiente"
      const reviewApplicationStatus = applicationStatuses.find(status => 
        status.name === 'Pendiente'
      ) || applicationStatuses.find(status => status.order_index === 2);
      const applicationStatusId = reviewApplicationStatus?.id || applicationStatuses[0]?.id || '';

      // 5. Validar fecha de visita técnica y convertir a boolean
      const hasValidTechnicalVisit = Boolean(
        tender.Fechas.FechaVisitaTerreno && 
        !isNaN(new Date(tender.Fechas.FechaVisitaTerreno).getTime())
      );

      // 6. Procesar items de la licitación con estructura corregida
      let items: TenderItem[] = [];
      let itemsCount = 0;

      if (tender.Items) {
        itemsCount = tender.Items.Cantidad || 0;
        
        if (tender.Items.Listado && tender.Items.Listado.length > 0) {
          items = tender.Items.Listado.map((item, index) => ({
            id: `item-${index}`,
            CodigoProducto: item.CodigoProducto || '',
            NombreProducto: item.NombreProducto || '',
            UnidadMedida: item.UnidadMedida || '',
            Cantidad: item.Cantidad || 0,
            // Extraer datos de adjudicación desde la estructura correcta
            RutProveedor: item.Adjudicacion?.RutProveedor || '',
            NombreProveedor: item.Adjudicacion?.NombreProveedor || '',
            MontoUnitario: item.Adjudicacion?.MontoUnitario || 0
          }));
        }
      }

      // 7. Crear nueva licitación
      const newTenderData: Omit<Tender, 'id' | 'company_id' | 'created_at'> = {
        tender_code: tender.CodigoExterno,
        title: tender.Nombre,
        description: tender.Descripcion,
        organization_id: organizationId,
        type_id: tenderTypeId,
        status_id: statusId,
        application_status_id: applicationStatusId,
        amount: tender.MontoEstimado || 0,
        currency: mapCurrency(tender.Moneda), // Use the mapping function
        contract_months: tender.Tiempo || undefined,
        publish_date: new Date(tender.Fechas.FechaCreacion).toISOString(),
        closing_date: new Date(tender.Fechas.FechaCierre).toISOString(),
        award_date: tender.Fechas.FechaAdjudicacion ? 
          new Date(tender.Fechas.FechaAdjudicacion).toISOString() : undefined,
        has_technical_visit: hasValidTechnicalVisit,
        technical_visit_date: hasValidTechnicalVisit ? 
          new Date(tender.Fechas.FechaVisitaTerreno!).toISOString() : undefined,
        fecha_preguntas: tender.Fechas.FechaFinal ? 
          new Date(tender.Fechas.FechaFinal).toISOString() : undefined,
        requirements: '',
        has_gso: false,
        gso_amount: undefined,
        has_gfc: false,
        gfc_amount: undefined,
        documents: [],
        items_count: itemsCount,
        items: items
      };

      await addTender(newTenderData);

      // Mostrar mensaje de éxito
      alert(`Licitación importada exitosamente con estado "${tender.Estado}" y ${items.length} items`);
      onClose();

    } catch (error) {
      console.error('Error importing tender:', error);
      alert('Error al importar la licitación. Por favor, intenta nuevamente.');
    } finally {
      setIsLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    try {
      return new Date(dateString).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const formatCurrency = (amount: number, currency: string) => {
    if (!amount) return 'No especificado';
    return `${currency} ${amount.toLocaleString('es-CL')}`;
  };

  return (
    <div className="fixed inset-0 bg-gray-600 bg-opacity-75 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg shadow-xl max-w-6xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex justify-between items-center mb-6">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Buscar Licitación</h2>
              <p className="text-gray-600">Consulta licitaciones en Mercado Público por ID</p>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearch} className="mb-6">
            <div className="flex gap-4">
              <div className="flex-1">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  ID de Licitación
                </label>
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchId}
                    onChange={(e) => setSearchId(e.target.value)}
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="Ej: 1234-567-L123"
                    disabled={isLoading}
                  />
                </div>
              </div>
              <div className="flex items-end">
                <button
                  type="submit"
                  disabled={isLoading}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center"
                >
                  {isLoading ? (
                    <>
                      <Loader className="h-5 w-5 mr-2 animate-spin" />
                      Buscando...
                    </>
                  ) : (
                    <>
                      <Search className="h-5 w-5 mr-2" />
                      Buscar
                    </>
                  )}
                </button>
              </div>
            </div>
          </form>

          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <p className="text-red-700 text-sm">{error}</p>
            </div>
          )}

          {/* Tender Results */}
          {tender && (
            <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
              <div className="p-6">
                <div className="flex justify-between items-start mb-4">
                  <div className="flex-1">
                    <div className="flex items-center mb-2">
                      <span className="text-sm font-mono text-blue-600 bg-blue-50 px-3 py-1 rounded-full">
                        {tender.CodigoExterno}
                      </span>
                      <span className={`ml-3 inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        tender.Estado === 'Publicada' ? 'bg-green-100 text-green-800' :
                        tender.Estado === 'Cerrada' ? 'bg-red-100 text-red-800' :
                        tender.Estado === 'Adjudicada' ? 'bg-blue-100 text-blue-800' :
                        tender.Estado === 'Desierta' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {tender.Estado || 'Estado no disponible'}
                      </span>
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">
                      {tender.Nombre}
                    </h3>
                    <p className="text-gray-600 mb-4">
                      {tender.Descripcion}
                    </p>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
                  {/* Organismo */}
                  <div className="flex items-start">
                    <div className="p-2 bg-blue-100 rounded-lg mr-3">
                      <Building2 className="h-5 w-5 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Organismo</p>
                      <p className="text-sm text-gray-900">{tender.Comprador.NombreOrganismo || 'No especificado'}</p>
                    </div>
                  </div>

                  {/* Tipo */}
                  <div className="flex items-start">
                    <div className="p-2 bg-purple-100 rounded-lg mr-3">
                      <FileText className="h-5 w-5 text-purple-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Tipo</p>
                      <p className="text-sm text-gray-900">{tender.Tipo?.Nombre || 'No especificado'}</p>
                    </div>
                  </div>

                  {/* Monto */}
                  <div className="flex items-start">
                    <div className="p-2 bg-green-100 rounded-lg mr-3">
                      <DollarSign className="h-5 w-5 text-green-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Monto Estimado</p>
                      <p className="text-sm text-gray-900 font-semibold">
                        {formatCurrency(tender.MontoEstimado, tender.Moneda)}
                      </p>
                    </div>
                  </div>

                  {/* Items */}
                  {tender.Items && (
                    <div className="flex items-start">
                      <div className="p-2 bg-orange-100 rounded-lg mr-3">
                        <Package className="h-5 w-5 text-orange-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Items Solicitados</p>
                        <p className="text-sm text-gray-900 font-semibold">
                          {tender.Items.Cantidad || 0} unidades
                        </p>
                        {tender.Items.Listado && tender.Items.Listado.length > 0 && (
                          <p className="text-xs text-gray-500">
                            {tender.Items.Listado.length} item{tender.Items.Listado.length !== 1 ? 's' : ''}
                          </p>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Fecha Publicación */}
                  <div className="flex items-start">
                    <div className="p-2 bg-yellow-100 rounded-lg mr-3">
                      <Calendar className="h-5 w-5 text-yellow-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Fecha Publicación</p>
                      <p className="text-sm text-gray-900">{formatDate(tender.Fechas.FechaCreacion)}</p>
                    </div>
                  </div>

                  {/* Fecha Cierre */}
                  <div className="flex items-start">
                    <div className="p-2 bg-red-100 rounded-lg mr-3">
                      <Calendar className="h-5 w-5 text-red-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-700">Fecha Cierre</p>
                      <p className="text-sm text-gray-900">{formatDate(tender.Fechas.FechaCierre)}</p>
                    </div>
                  </div>

                  {/* Fecha Límite para Preguntas */}
                  {tender.Fechas.FechaFinal && (
                    <div className="flex items-start">
                      <div className="p-2 bg-purple-100 rounded-lg mr-3">
                        <Calendar className="h-5 w-5 text-purple-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Fecha Límite Preguntas</p>
                        <p className="text-sm text-gray-900">{formatDate(tender.Fechas.FechaFinal)}</p>
                      </div>
                    </div>
                  )}

                  {/* Fecha Visita Técnica */}
                  {tender.Fechas.FechaVisitaTerreno && (
                    <div className="flex items-start">
                      <div className="p-2 bg-indigo-100 rounded-lg mr-3">
                        <Calendar className="h-5 w-5 text-indigo-600" />
                      </div>
                      <div>
                        <p className="text-sm font-medium text-gray-700">Visita Técnica</p>
                        <p className="text-sm text-gray-900">{formatDate(tender.Fechas.FechaVisitaTerreno)}</p>
                      </div>
                    </div>
                  )}
                </div>

                {/* Items Detail Grid - Con estructura corregida */}
                {tender.Items && tender.Items.Listado && tender.Items.Listado.length > 0 && (
                  <div className="mt-6 p-4 bg-gray-50 rounded-lg">
                    <h4 className="text-sm font-medium text-gray-900 mb-3 flex items-center">
                      <Package className="h-4 w-4 mr-2" />
                      Items de la Licitación ({tender.Items.Listado.length})
                    </h4>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead className="bg-gray-100">
                          <tr>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Item</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Unidad</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cantidad</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Proveedor</th>
                            <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Monto Unit.</th>
                          </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                          {tender.Items.Listado.slice(0, 10).map((item, index) => (
                            <tr key={index} className="hover:bg-gray-50">
                              <td className="px-3 py-2">
                                <div className="font-medium text-gray-900">{item.NombreProducto}</div>
                                {item.CodigoProducto && (
                                  <div className="text-xs text-gray-500 font-mono">{item.CodigoProducto}</div>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                <div>{item.UnidadMedida || '-'}</div>
                              </td>
                              <td className="px-3 py-2">
                                <div className="font-medium">{item.Cantidad.toLocaleString()}</div>
                              </td>
                              <td className="px-3 py-2">
                                {item.Adjudicacion?.RutProveedor && item.Adjudicacion?.NombreProveedor ? (
                                  <div className="space-y-1">
                                    <div className="flex items-center text-xs">
                                      <Award className="h-3 w-3 text-green-600 mr-1" />
                                      <span className="font-medium text-green-700">Adjudicado</span>
                                    </div>
                                    <div className="text-xs text-gray-600">
                                      <div className="font-medium">{item.Adjudicacion.NombreProveedor}</div>
                                      <div className="font-mono">{item.Adjudicacion.RutProveedor}</div>
                                      {item.Adjudicacion.Cantidad && (
                                        <div className="text-blue-600">Cant. Adj: {item.Adjudicacion.Cantidad.toLocaleString()}</div>
                                      )}
                                    </div>
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">Sin adjudicar</span>
                                )}
                              </td>
                              <td className="px-3 py-2">
                                {item.Adjudicacion?.MontoUnitario ? (
                                  <div className="font-medium text-green-600">
                                    {formatCurrency(item.Adjudicacion.MontoUnitario, tender.Moneda)}
                                  </div>
                                ) : (
                                  <span className="text-xs text-gray-400">-</span>
                                )}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {tender.Items.Listado.length > 10 && (
                        <div className="text-xs text-gray-500 text-center py-2">
                          +{tender.Items.Listado.length - 10} items más...
                        </div>
                      )}
                    </div>

                    {/* Resumen de adjudicaciones */}
                    {tender.Items.Listado.some(item => item.Adjudicacion?.RutProveedor && item.Adjudicacion?.NombreProveedor) && (
                      <div className="mt-4 bg-green-50 border border-green-200 rounded-lg p-3">
                        <div className="flex items-center mb-2">
                          <Award className="h-4 w-4 text-green-600 mr-2" />
                          <span className="text-sm font-medium text-green-900">Adjudicaciones encontradas</span>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-xs">
                          <div>
                            <span className="text-green-700 font-medium">Items adjudicados:</span>
                            <span className="ml-2 text-green-900">
                              {tender.Items.Listado.filter(item => item.Adjudicacion?.RutProveedor && item.Adjudicacion?.NombreProveedor).length} de {tender.Items.Listado.length}
                            </span>
                          </div>
                          <div>
                            <span className="text-green-700 font-medium">Proveedores únicos:</span>
                            <span className="ml-2 text-green-900">
                              {new Set(tender.Items.Listado.filter(item => item.Adjudicacion?.RutProveedor).map(item => item.Adjudicacion!.RutProveedor)).size}
                            </span>
                          </div>
                          <div>
                            <span className="text-green-700 font-medium">Monto total adjudicado:</span>
                            <span className="ml-2 text-green-900 font-medium">
                              {formatCurrency(
                                tender.Items.Listado
                                  .filter(item => item.Adjudicacion?.MontoUnitario && item.Adjudicacion?.RutProveedor)
                                  .reduce((sum, item) => sum + (item.Adjudicacion!.MontoUnitario * (item.Adjudicacion!.Cantidad || item.Cantidad)), 0),
                                tender.Moneda
                              )}
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Action Buttons */}
              <div className="mt-6 pt-4 border-t border-gray-200 flex items-center justify-between">
                {tenderExists && (
                  <div className="flex items-center text-amber-600 bg-amber-50 px-3 py-2 rounded-lg">
                    <FileText className="h-4 w-4 mr-2" />
                    <span className="text-sm font-medium">Esta licitación ya existe en el sistema</span>
                  </div>
                )}
                <button
                  onClick={handleImportTender}
                  disabled={isLoading || tenderExists}
                  className="bg-green-600 text-white px-4 py-2 rounded-lg font-medium hover:bg-green-700 transition-colors flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                  title={tenderExists ? 'Esta licitación ya existe en el sistema' : `Importar licitación con estado "${tender.Estado}" y todos sus items`}
                >
                  {isLoading ? (
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <FileText className="h-4 w-4 mr-2" />
                  )}
                  {isLoading ? 'Importando...' : tenderExists ? 'Ya Importada' : `Importar con Estado "${tender.Estado}"`}
                </button>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className="mt-6 p-4 bg-blue-50 border border-blue-200 rounded-lg">
            <div className="flex items-start">
              <div className="p-1 bg-blue-100 rounded-lg mr-3">
                <FileText className="h-4 w-4 text-blue-600" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-blue-900 mb-1">Información</h4>
                <p className="text-sm text-blue-700">
                  Esta búsqueda consulta directamente la API pública de Mercado Público de Chile. 
                  Los resultados muestran información oficial y actualizada de las licitaciones, incluyendo los items solicitados y sus adjudicaciones.
                  El estado de la licitación se importa tal como viene de la API.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TenderSearch;